import React from "react";
import ReactDOM from "react-dom";
import { IResiStore, UpdateFn } from "@base/resi/IResiStore";
import { createSubStore } from "@base/resi/ResiRef";
import { createResiStore } from "@base/resi/ResiStore";
import { EqualsFunctionOption } from "@base/util/CompareUtil";
import { ImmHelper } from "@base/util/Immutable";
import { getPathFromSelector } from "@base/util/PathFromSelector";
import { AnyFunction, getKeys, PickByType } from "@base/util/TypeUtil";

export type PartialState<T extends object, K extends keyof T = keyof T> = (Pick<T, K> | T) | ((state: T) => Pick<T, K> | T);

export type Setter<S extends object> = (stateOrFn: S | UpdateFn<S>) => void;
// export type Merger<S extends object> = { <K extends keyof S>(stateOrFn: PartialState<S, K>, replace?: boolean): void;};
export type ReducerFunction<S = any, A extends any[] = any[]> = (old: S, ...args: A) => S;
export type InitialStateFunction<S> = () => S;
export type ReducerFunctionArgs<T extends ReducerFunction> = T extends ReducerFunction<any, infer A> ? A : never; // Tail<Parameters<T>>; // T extends (old: any, ...args: infer A) => any ? A : never;

export type ParametersFromReducerFunction<T extends AnyFunction> = T extends (state: any, ...args: infer P) => any ? P : never;
export type DispatcherFromReducerFunction<T extends AnyFunction> = T extends (state: any, ...args: infer P) => any ? (...args: P) => void : never;

export type ReducerObject<T = any> = Record<string, ReducerFunction<T>>;
export type DispatcherMap<T extends ReducerObject> = {
  [P in keyof T]: (...params: ReducerFunctionArgs<T[P]>) => Parameters<T[P]>[0]; // void ?
};

export type Dispatcher<R extends ReducerObject, S> = DispatcherMap<PickByType<R, ReducerFunction<S>>>;

/*export function castDispatcher<R extends Record<string, ReducerFunction<M>>, S extends M, M>(d: Dispatcher<R, M>, m: M) {
  return d as any as Dispatcher<R, M>;
}*/

export type Selector<FROM, TO> = (old: FROM) => TO;
export type Updater<FROM, TO> = (old: FROM, value: TO) => FROM;
export const identitySelector = <FROM>(from: FROM): FROM => from;

// export type ReducerMap<STATE> = Record<string, ReducerFunction<STATE>>;
export interface ReducerMap<STATE> {
  [K: string]: ReducerFunction<STATE>;
}

export interface SliceFactory<STATE extends object, R extends ReducerObject, P = object> {
  fromStore(store: IResiStore<STATE>): ISlice<STATE, R> & P;
  fromStore<PARENT_STATE extends object>(store: IResiStore<PARENT_STATE>, selector: Selector<PARENT_STATE, STATE>, updater?: Updater<PARENT_STATE, STATE>): ISlice<STATE, R> & P;
  fromStore<PARENT_STATE extends object>(store: IResiStore<PARENT_STATE>, selector: Selector<PARENT_STATE, STATE | undefined>, updater?: Updater<PARENT_STATE, STATE>): (ISlice<STATE, R> & P) | undefined;
  attach<P2>(fn: (item: (ISlice<STATE, R> & P)) => P2): (SliceFactory<STATE, R, P & P2>);
  reducers: R,
  _upgraders: ((slice: ISlice<STATE, R>) => any)[];
}

export type SliceTypeFromFactory<SF> = SF extends SliceFactory<infer S, infer R, infer P> ? (ISlice<S, R> & P) : never;

export class Resi {
  static createDispatcherFromSetter<P extends object, S extends object, R extends ReducerObject>(setter: Setter<P>, select: Selector<P, S>, reducer?: R, init?: () => S): Dispatcher<R, S>;
  static createDispatcherFromSetter<S extends object, R extends ReducerObject>(setter: Setter<S>, reducer?: R, init?: () => S): Dispatcher<R, S>;
  static createDispatcherFromSetter(setter: Setter<any>, p1?: any, p2?: any, p3?: any): Dispatcher<any, any> {
    if (typeof p1 === "function") {
      const subSetter = Resi.createSubSetter(setter, p1) as Setter<any>;
      return this.createDispatcherProxy(subSetter, p2, p3);
    } else {
      return this.createDispatcherProxy(setter, p1, p2);
    }
  }

  static createDispatcherProxy<S extends object, R extends ReducerObject>(setter: Setter<S>, reducer: R, init?: () => S): Dispatcher<R, S> {
    const handler = {
      ownKeys: () => {
        const keys = Object.keys(reducer);
        return keys;
      },
      getOwnPropertyDescriptor(_target: any, _prop: string) { // called for every property
        return {
          enumerable: true,
          configurable: true
        };
      },
      get: function (_target: any, prop: any, _receiver: any) {
        return (...args: any) => {
          // if (prop === "_as") return self;
          const fn = reducer?.[prop];
          if (!fn) {
            throw new Error("No function found!" + prop);
          }
          return setter((old) => {
            if (!old && init) old = init();
            if (!old) {
              throw new Error("State to reduce with " + prop + " is null!");
            }
            return fn(old, ...args);
          });
        };
      },
    };
    const self = new Proxy({}, handler);
    return self;
  }

  static createDispatcherProxyNew<S extends object, R extends ReducerObject<S>>(getter: () => S, setter: (s: S) => void, reducer: R, init?: () => S): Dispatcher<R, S> {
    const handler = {
      ownKeys: () => {
        return Object.keys(reducer);
      },
      get: function (_target: any, prop: any, _receiver: any) {
        return (...args: any) => {
          // if (prop === "_as") return self;
          const fn = reducer[prop];
          if (!fn) {
            throw new Error("No function found!" + prop);
          }
          let old = getter() ?? init?.();
          if (!old) {
            throw new Error("State to reduce with " + prop + " is null!");
          }
          old = fn(old, ...args);
          return setter(old);
        };
      },
    };
    const self = new Proxy({}, handler);
    return self;
  }

  static createDispatcherObject<S extends object, R extends ReducerObject>(setter: Setter<S>, reducer: R, init?: () => S): Dispatcher<R, S> {
    const dispatcher: any = {};
    const keys = getKeys(reducer);
    for (const k of keys) {
      dispatcher[k] = (...params: any) =>
        setter((old) => {
          if (!old && init) old = init();
          return reducer[k](old, ...params);
        });
    }
    return dispatcher;
  }

  static useDispatcher<STATE extends object, REDUCER_OBJECT extends ReducerObject>(setter: Setter<STATE>, reducerObject: REDUCER_OBJECT, init?: () => STATE): Dispatcher<REDUCER_OBJECT, STATE> {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return React.useMemo(() => Resi.createDispatcherObject(setter, reducerObject, init), [setter, reducerObject, init]);
  }

  static createSliceWithStore<STATE extends object, R extends ReducerObject>(name: string, params: { init: () => STATE; reducers?: R; hot: any }): ISlice<STATE, R> {
    const store = createResiStore<STATE>(name, params.init(), params.hot);
    const slice = Resi.createSlice<STATE, R>(store, { reducers: params.reducers, init: params.init });
    return slice;
  }

  static createSlice<STATE extends object, R extends ReducerObject>(store: IResiStore<STATE>, params?: { reducers?: R; init?: () => STATE; }): ISlice<STATE, R> {
    const template = this.createSliceFactory(params);
    return template.fromStore(store);
  }

  // static createSubStore<STORE_STATE extends object, REF_STATE>(store: IResiStore<STORE_STATE>, selector: Selector<STORE_STATE, REF_STATE>, updater?: Updater<STORE_STATE, REF_STATE>): IResiStore<REF_STATE> {
  static createSliceFactory<STATE extends object, R extends ReducerObject>(params?: { reducers?: R; init?: () => STATE; }): SliceFactory<STATE, R> {
    function _fromStore(store: IResiStore<STATE>): ISlice<STATE, R> {
      const setter: Setter<STATE> = Resi.getSetter(store);
      const dispatcher = Resi.createDispatcherFromSetter(setter, params?.reducers, params?.init);
      return {
        store: store,
        reducers: params?.reducers as R,
        dispatcher,
        setter,
        init: params?.init,
        // getDispatcher: (setter: Setter<STATE>) => getDispatcher(setter, reducer),
        useSelector(selector?: any, options?: { equalityFn?: EqualsFunctionOption; debugName?: string }): any {
          return store.useSelector(selector || identitySelector, options);
        },
      };
    }

    function _fromSubStore<PARENT_STATE extends object>(store: IResiStore<PARENT_STATE>, selector: Selector<PARENT_STATE, STATE>, updater?: Updater<PARENT_STATE, STATE>): ISlice<STATE, R> {
      const subStore = Resi.createSubStore(store, selector, updater);
      return _fromStore(subStore);
    }

    return {
      fromStore(store: any, selector?: any, updater?: any) {
        let slice = selector ? _fromSubStore(store, selector, updater) : _fromStore(store);
        for (const upgrade of this._upgraders) {
          slice = {
            ...slice,
            ...upgrade(slice)
          }
        }
        return slice;
      },
      attach(fn) {
        this._upgraders.push(fn);
        return this as any;
      },
      reducers: (params?.reducers || {}) as R,
      _upgraders: []
    }
  }

  static createSubSlice<PARENT_STATE extends object, STATE extends object, R extends ReducerObject>(
    store: IResiStore<PARENT_STATE>,
    selector: Selector<PARENT_STATE, STATE>,
    params?: { reducers?: R; init?: () => STATE; updater?: Updater<PARENT_STATE, STATE>; }
  ): ISlice<STATE, R> {
    const subStore = this.createSubStore(store, selector, params?.updater);
    const slice = this.createSlice(subStore, {
      reducers: params?.reducers,
      init: params?.init,
    });
    return slice;
  }

  static forState<STATE>(): {
    check: <R extends ReducerMap<STATE>>(params: { init: () => STATE; reducers: R }) => { init: () => STATE; reducers: R };
  } {
    return {
      check: ({ init, reducers }) => {
        return {
          init,
          reducers,
        };
      },
    };
  }

  static batch(callback: () => any): void {
    ReactDOM.unstable_batchedUpdates(callback);
  }

  static getSetter<S extends object>(store: IResiStore<S>): Setter<S> {
    return store.set.bind(store);
  }

  /**
   * creates a setter from a root setter and a selector
   * !!! experimental !!!
   * @param setter the root setter
   * @param selector the selector for the sub setter
   * @returns
   */
  static createSubSetter<S extends object, R extends object>(setter: Setter<S>, selector: (state: S) => R): Setter<R> {
    const path = getPathFromSelector(selector);
    return (newStateOrFn) => {
      if (typeof newStateOrFn === "function") {
        let newRef: R | undefined;
        setter((old: S): S => {
          const oldRef = selector(old);
          newRef = (newStateOrFn as UpdateFn<R>)(oldRef);
          return oldRef !== newRef ? ImmHelper.setPropertyPath(old, path, newRef, true) : old;
        });
        return newRef!;
      } else {
        let newRef: R | undefined;
        setter((old: S): S => {
          const oldRef = selector(old);
          return oldRef !== newStateOrFn ? ImmHelper.setPropertyPath(old, path, newStateOrFn, true) : old;
        });
        return newRef!;
      }
    };
  }

  static createSubStore<STORE_STATE extends object, REF_STATE extends object>(store: IResiStore<STORE_STATE>, selector: Selector<STORE_STATE, REF_STATE>, updater?: Updater<STORE_STATE, REF_STATE>): IResiStore<REF_STATE> {
    return createSubStore(store, selector, updater);
  }

  static createStore<S extends object>(name: string, initialState: S): IResiStore<S> {
    return createResiStore(name, initialState);
  }

  /*static createSubMerger<S extends object, R extends object>(setter: Setter<S>, selector: (state: S) => R): Merger<R> {
    const path = getPathFromSelector(selector);
    return (newStateOrFn, replace) => {
      if (typeof newStateOrFn === "function") {
        let newRef: R | undefined;
        setter((old: S): S => {
          const oldRef = selector(old);
          newRef = (newStateOrFn as UpdateFn<R>)(oldRef);
          return oldRef !== newRef ? (replace ? ImmHelper.setPropertyPath(old, path, newRef) : ImmHelper.mergePropertyPath(old, path, newRef)) : old;
        });
        return newRef!;
      } else {
        let newRef: R | undefined;
        setter((old: S): S => {
          const oldRef = selector(old);
          return oldRef !== newStateOrFn ? (replace ? ImmHelper.setPropertyPath(old, path, newStateOrFn) : ImmHelper.mergePropertyPath(old, path, newStateOrFn)) : old;
        });
        return newRef!;
      }
    };
  }*/

  static attach<T, P>(item: T, fn: (item: T) => P): T & P {
    return {
      ...item,
      ...fn(item)
    }
  }
}

export type UseSelectorOptions = { equalityFn?: EqualsFunctionOption; debugName?: string };

export interface ISelectorDescription<ARGS extends any[], R> {
  fn: (...args: ARGS) => R;
  options?: UseSelectorOptions
}

export type ISliceSelector<STATE extends (object | undefined)> = <SEL>(selector: (state: STATE) => SEL, options?: UseSelectorOptions) => SEL;
type ExtractStateFromSliceSelector<T> = T extends ISliceSelector<infer R> ? R : never; // IVariantData2d | IVariantData3d
export function createSubSelector<SS extends ISliceSelector<any>, R extends (object | undefined)>(
  useSelector: SS,
  selector: (state: ExtractStateFromSliceSelector<SS>) => R,
): ISliceSelector<R> {
  return (selector2, options) => useSelector((state) => selector2(selector(state)), options);
}

export interface ISlice<STATE extends object, R extends ReducerObject> {
  store: IResiStore<STATE>;
  setter: Setter<STATE>;
  init?: () => STATE;
  useSelector: ISliceSelector<STATE>;
  reducers: R;
  dispatcher: Dispatcher<R, STATE>;
}

export type FunctionDispatcher<S, RO extends ReducerMap<S>> = <RN extends keyof RO>(fn: RN, ...args: ReducerFunctionArgs<RO[RN]>) => void;

export function getFunctionDispatcher<S extends object, RM extends ReducerMap<S>>(setter: Setter<S>, reducerMap: RM, init?: () => S): FunctionDispatcher<S, RM> {
  return (fn, ...args): void => {
    setter((old: S) => {
      if (!old && init) old = init();
      return (reducerMap as any)[fn](old, ...args);
    });
  };
}